import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { supabase } from '../supabaseClient';
import { createClient } from '@supabase/supabase-js';
import { 
    FaDatabase, FaUndo, FaCogs, FaInfoCircle, FaUsers, FaUserShield, 
    FaUserAlt, FaIndustry, FaSpinner, FaCheck, FaPlus, FaDownload, 
    FaFileExcel, FaFileCode, FaPaintBrush, FaCalendarAlt, FaMagic, 
    FaEye, FaSnowflake, FaCheckCircle, FaExchangeAlt, FaTrashAlt, FaHdd
} from 'react-icons/fa';
import { useSeasonalTheme } from '../contexts/SeasonalThemeContext';
import '../styles/Settings.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const roleBadgeStyles = {
    admin: { background: '#dbeafe', color: '#1d4ed8' },
    client: { background: '#dcfce7', color: '#166534' },
    producteur: { background: '#f3e8ff', color: '#7c3aed' },
};

const roleIcons = {
    admin: <FaUserShield />,
    client: <FaUserAlt />,
    producteur: <FaIndustry />,
};

const Settings = () => {
    const { resetProducts, products, purgeAllInvoicedSTLFiles, orders } = useData();
    const seasonal = useSeasonalTheme();

    const [profiles, setProfiles] = useState([]);
    const [loadingProfiles, setLoadingProfiles] = useState(true);
    const [savingRole, setSavingRole] = useState(null);
    const [purgingSTL, setPurgingSTL] = useState(false);
    const [purgeResult, setPurgeResult] = useState('');

    const handlePurgeInvoicedSTL = async () => {
        const invoicedOrders = (orders || []).filter(o => 
            o.status && (o.status.toLowerCase() === 'facturé' || o.status.toLowerCase() === 'facture')
        );
        if (invoicedOrders.length === 0) {
            alert("Aucun cas facturé trouvé dans le système.");
            return;
        }

        if (!window.confirm(`Nettoyer le stockage 3D pour les ${invoicedOrders.length} cas facturés ? Cette opération supprimera définitivement les fichiers STL volumineux du cloud Supabase pour préserver vos quotas.`)) {
            return;
        }

        setPurgingSTL(true);
        setPurgeResult('');
        try {
            const res = await purgeAllInvoicedSTLFiles();
            setPurgeResult(`Nettoyage réussi : ${res.totalPurgedFiles} fichier(s) STL supprimé(s) du cloud sur ${res.totalInvoicedScanned} cas facturé(s).`);
        } catch (err) {
            console.error("Erreur purge STL:", err);
            alert("Erreur lors de la suppression des fichiers STL.");
        } finally {
            setPurgingSTL(false);
        }
    };

    // Form states for creating a new user
    const [showAddForm, setShowAddForm] = useState(false);
    const [newUser, setNewUser] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'producteur'
    });
    const [creatingUser, setCreatingUser] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [exporting, setExporting] = useState(false);

    const handleExportBackup = async (format = 'json') => {
        setExporting(true);
        try {
            const [
                { data: ordersData },
                { data: invoicesData },
                { data: clientsData },
                { data: productsData },
                { data: paymentsData },
                { data: creditNotesData },
                { data: profilesData }
            ] = await Promise.all([
                supabase.from('orders').select('*'),
                supabase.from('invoices').select('*'),
                supabase.from('clients').select('*'),
                supabase.from('products').select('*'),
                supabase.from('payments').select('*'),
                supabase.from('credit_notes').select('*'),
                supabase.from('profiles').select('id, email, full_name, role')
            ]);

            const dateStr = new Date().toISOString().split('T')[0];

            if (format === 'json') {
                const fullBackup = {
                    version: "1.0",
                    exportDate: new Date().toISOString(),
                    laboratory: "ENOVADENT",
                    summary: {
                        totalOrders: (ordersData || []).length,
                        totalInvoices: (invoicesData || []).length,
                        totalClients: (clientsData || []).length,
                        totalPayments: (paymentsData || []).length,
                    },
                    data: {
                        orders: ordersData || [],
                        invoices: invoicesData || [],
                        clients: clientsData || [],
                        products: productsData || [],
                        payments: paymentsData || [],
                        creditNotes: creditNotesData || [],
                        profiles: profilesData || []
                    }
                };

                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(fullBackup, null, 2));
                const downloadAnchor = document.createElement('a');
                downloadAnchor.setAttribute("href", dataStr);
                downloadAnchor.setAttribute("download", `enovadent-sauvegarde-complete-${dateStr}.json`);
                document.body.appendChild(downloadAnchor);
                downloadAnchor.click();
                downloadAnchor.remove();
            } else if (format === 'csv') {
                let csvContent = "data:text/csv;charset=utf-8,";
                csvContent += "Type,ID / N° Document,Date,Client / Praticien,Patient,Montant (EUR),Statut\n";

                (invoicesData || []).forEach(inv => {
                    const row = [
                        "Facture",
                        `"${inv.invoice_number || inv.id}"`,
                        `"${inv.date ? new Date(inv.date).toLocaleDateString() : ''}"`,
                        `"${inv.practitioner_name || ''}"`,
                        `"${inv.patient_name || ''}"`,
                        `"${inv.amount || inv.total_amount || 0}"`,
                        `"${inv.status || ''}"`
                    ].join(",");
                    csvContent += row + "\n";
                });

                (ordersData || []).forEach(ord => {
                    const row = [
                        "Commande",
                        `"${ord.case_number || ord.id}"`,
                        `"${ord.created_at ? new Date(ord.created_at).toLocaleDateString() : ''}"`,
                        `"${ord.practitioner_name || ''}"`,
                        `"${ord.patient_name || ''}"`,
                        `"-"`,
                        `"${ord.status || ''}"`
                    ].join(",");
                    csvContent += row + "\n";
                });

                const encodedUri = encodeURI(csvContent);
                const link = document.createElement("a");
                link.setAttribute("href", encodedUri);
                link.setAttribute("download", `enovadent-recapitulatif-${dateStr}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (err) {
            console.error("Erreur lors de l'exportation:", err);
            alert("Erreur lors du téléchargement de la sauvegarde.");
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        const fetchProfiles = async () => {
            setLoadingProfiles(true);
            const { data, error } = await supabase
                .from('profiles')
                .select('id, email, full_name, role')
                .order('created_at', { ascending: false });
            if (!error && data) setProfiles(data);
            setLoadingProfiles(false);
        };
        fetchProfiles();
    }, []);

    const handleRoleChange = async (profileId, newRole) => {
        setSavingRole(profileId);
        const { error } = await supabase
            .from('profiles')
            .update({ role: newRole })
            .eq('id', profileId);
        if (!error) {
            setProfiles(prev => prev.map(p => p.id === profileId ? { ...p, role: newRole } : p));
        }
        setSavingRole(null);
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!newUser.email || !newUser.password || !newUser.firstName || !newUser.lastName) {
            setErrorMsg('Veuillez remplir tous les champs obligatoires.');
            return;
        }
        setCreatingUser(true);
        setErrorMsg('');
        setSuccessMsg('');

        try {
            // Create a temporary client that does not persist session to avoid logging out the current admin
            const tempClient = createClient(supabaseUrl, supabaseAnonKey, {
                auth: { persistSession: false }
            });

            // 1. Sign up user
            const { data, error: signUpError } = await tempClient.auth.signUp({
                email: newUser.email,
                password: newUser.password,
                options: {
                    data: {
                        first_name: newUser.firstName,
                        last_name: newUser.lastName,
                        full_name: `${newUser.firstName} ${newUser.lastName}`,
                        role: newUser.role
                    }
                }
            });

            if (signUpError) throw signUpError;

            if (data?.user) {
                // 2. Ensure role is correctly set in profiles table
                const { error: profileError } = await supabase
                    .from('profiles')
                    .update({ role: newUser.role })
                    .eq('id', data.user.id);

                if (profileError) console.error('Error setting profile role:', profileError);

                // Add to local state
                const addedProfile = {
                    id: data.user.id,
                    email: newUser.email,
                    full_name: `${newUser.firstName} ${newUser.lastName}`,
                    role: newUser.role
                };
                setProfiles(prev => [addedProfile, ...prev]);

                setSuccessMsg(`Utilisateur "${newUser.firstName} ${newUser.lastName}" créé avec succès !`);
                setNewUser({
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                    role: 'producteur'
                });
                setShowAddForm(false);
            }
        } catch (err) {
            setErrorMsg(err.message || 'Erreur lors de la création');
        } finally {
            setCreatingUser(false);
        }
    };

    return (
        <div className="settings-page animate__animated animate__fadeIn">
            <div className="page-header mb-4">
                <h2 className="mb-1">Paramètres</h2>
                <p className="text-muted">Configuration globale de l'application ENOVADENT.</p>
            </div>

            <div className="settings-grid">
                {/* Seasonal Themes & Atmosphere Section */}
                <div className="settings-card shadow-sm border-0" style={{ borderRadius: '16px', gridColumn: '1 / -1' }}>
                    <div className="card-header bg-white border-bottom-0 pt-4 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                            <FaPaintBrush className="text-primary me-2" /> Ambiance & Thèmes Saisonniers
                        </h5>
                        <div className="d-flex align-items-center gap-2">
                            {seasonal.isSeasonalActive && (
                                <span className="seasonal-banner-badge" style={{ fontSize: '0.85rem', padding: '4px 12px' }}>
                                    {seasonal.themeMeta.icon} {seasonal.themeMeta.name} actif
                                </span>
                            )}
                            {seasonal.isOverridden && (
                                <button 
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                    onClick={seasonal.resetToAuto}
                                    style={{ borderRadius: '8px', fontSize: '0.8rem' }}
                                    title="Revenir à la détection automatique selon la date du jour"
                                >
                                    <FaExchangeAlt /> Rétablir l'automatique
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="card-body p-4">
                        <p className="text-muted small mb-4">
                            L'application adapte dynamiquement son thème visuel (couleurs, accents et douces animations) lors des fêtes de l'année (Saint-Valentin, Pâques, Halloween, Noël, etc.).
                        </p>

                        <div className="d-flex flex-wrap gap-4 align-items-center mb-4 pb-3 border-bottom">
                            {/* Switch principal : Activer les thèmes */}
                            <div className="form-check form-switch d-flex align-items-center gap-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="switchSeasonalThemes"
                                    checked={seasonal.enabled}
                                    onChange={(e) => seasonal.setEnabled(e.target.checked)}
                                    style={{ cursor: 'pointer', width: '2.5rem', height: '1.3rem' }}
                                />
                                <label className="form-check-label fw-bold small ms-1" htmlFor="switchSeasonalThemes" style={{ cursor: 'pointer' }}>
                                    Activer les thèmes saisonniers automatiques
                                </label>
                            </div>

                            {/* Switch secondaire : Décorations douces */}
                            <div className="form-check form-switch d-flex align-items-center gap-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="switchSeasonalDecorations"
                                    checked={seasonal.decorationsEnabled}
                                    onChange={(e) => seasonal.setDecorationsEnabled(e.target.checked)}
                                    disabled={!seasonal.enabled}
                                    style={{ cursor: 'pointer', width: '2.5rem', height: '1.3rem' }}
                                />
                                <label className="form-check-label small ms-1" htmlFor="switchSeasonalDecorations" style={{ cursor: 'pointer' }}>
                                    Animations & particules ambiantes (neige, cœurs, chauves-souris)
                                </label>
                            </div>
                        </div>

                        {/* Prévisualisateur interactif */}
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                    <FaEye className="text-primary" /> Prévisualiser ou tester un thème en direct :
                                </h6>
                                <span className="text-muted small">
                                    {seasonal.isOverridden ? 'Mode forcé actif (pour test)' : 'Détection automatique basée sur la date'}
                                </span>
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                gap: '12px'
                            }}>
                                {seasonal.availableThemes.map((th) => {
                                    const isSelected = seasonal.currentThemeId === th.id;
                                    const isNatural = seasonal.naturalThemeId === th.id;

                                    return (
                                        <div
                                            key={th.id}
                                            className={`theme-preview-card ${isSelected ? 'selected' : ''}`}
                                            onClick={() => seasonal.setThemeOverride(th.id === 'default' && !seasonal.isOverridden ? 'auto' : th.id)}
                                            style={{
                                                borderColor: isSelected ? th.colors.primary : '#e5e7eb',
                                                backgroundColor: isSelected ? '#ffffff' : '#fafafa'
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ fontSize: '1.4rem' }}>{th.icon}</span>
                                                    <span className="fw-bold small">{th.shortName}</span>
                                                </div>
                                                {isNatural && (
                                                    <span className="badge bg-success-light text-success" style={{ fontSize: '0.65rem' }}>
                                                        Aujourd'hui
                                                    </span>
                                                )}
                                                {isSelected && (
                                                    <FaCheckCircle style={{ color: th.colors.primary }} />
                                                )}
                                            </div>

                                            <div className="text-muted" style={{ fontSize: '0.72rem', minHeight: '34px' }}>
                                                {th.datesDescription}
                                            </div>

                                            {/* Nuancier de couleurs */}
                                            <div className="d-flex align-items-center gap-1 mt-2">
                                                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: th.colors.primary, display: 'inline-block' }} title="Couleur Principale" />
                                                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: th.colors.secondary, display: 'inline-block' }} title="Couleur Secondaire" />
                                                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: th.colors.accent, display: 'inline-block' }} title="Accent" />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Management Section */}
                <div className="settings-card shadow-sm border-0" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                            <FaDatabase className="text-primary me-2" /> Gestion des Données
                        </h5>
                    </div>
                    <div className="card-body p-4">
                        <div className="d-flex align-items-start gap-3 mb-4 pb-4 border-bottom">
                            <div className="bg-light p-3 rounded-circle text-primary">
                                <FaUndo size={24} />
                            </div>
                            <div>
                                <h6 className="fw-bold mb-1">Catalogue des Produits</h6>
                                <p className="text-muted small mb-3">
                                    Si vous ne voyez pas tous les produits ou catégories (ex: Implant, Zircone),
                                    vous pouvez réinitialiser le catalogue aux valeurs par défaut.
                                </p>
                                <div className="alert alert-info border-0 shadow-sm small d-flex align-items-center mb-3" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                                    <FaInfoCircle className="me-2" />
                                    Votre catalogue actuel contient <strong>{products.length}</strong> produits.
                                </div>
                                <button
                                    className="btn btn-primary d-flex align-items-center gap-2 px-4 shadow-sm fw-bold"
                                    onClick={resetProducts}
                                    style={{ borderRadius: '10px' }}
                                >
                                    <FaUndo /> Réinitialiser le catalogue par défaut
                                </button>
                                <p className="text-danger small mt-2 fw-bold">
                                    Attention : Cela écrasera vos modifications de tarifs personnalisées.
                                </p>
                            </div>
                        </div>

                        {/* Export & Backup Section */}
                        <div className="d-flex align-items-start gap-3">
                            <div className="bg-light p-3 rounded-circle text-success">
                                <FaDownload size={24} />
                            </div>
                            <div>
                                <h6 className="fw-bold mb-1">Sauvegarde & Exporter l'intégralité des données</h6>
                                <p className="text-muted small mb-3">
                                    Téléchargez une sauvegarde externe complète de la plateforme (commandes, factures, récapitulatifs clients) pour archivage mensuel en toute sécurité.
                                </p>
                                <div className="d-flex flex-wrap gap-2">
                                    <button
                                        className="btn btn-success d-flex align-items-center gap-2 px-4 shadow-sm fw-bold"
                                        onClick={() => handleExportBackup('json')}
                                        disabled={exporting}
                                        style={{ borderRadius: '10px' }}
                                    >
                                        {exporting ? <FaSpinner className="fa-spin" /> : <FaFileCode />}
                                        Télécharger Sauvegarde Complète (JSON)
                                    </button>
                                    <button
                                        className="btn btn-outline-success d-flex align-items-center gap-2 px-4 shadow-sm fw-bold"
                                        onClick={() => handleExportBackup('csv')}
                                        disabled={exporting}
                                        style={{ borderRadius: '10px' }}
                                    >
                                        {exporting ? <FaSpinner className="fa-spin" /> : <FaFileExcel />}
                                        Exporter Récapitulatif Factures & Commandes (Excel/CSV)
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* STL Storage Optimization Section */}
                        <div className="d-flex align-items-start gap-3 mt-4 pt-4 border-top">
                            <div className="bg-light p-3 rounded-circle text-warning">
                                <FaHdd size={24} style={{ color: '#d97706' }} />
                            </div>
                            <div className="flex-grow-1">
                                <h6 className="fw-bold mb-1">Optimisation & Nettoyage du Stockage 3D (STL)</h6>
                                <p className="text-muted small mb-3">
                                    Les fichiers 3D (STL) sont désormais automatiquement supprimés du cloud Supabase dès qu'un cas passe en <strong>Facturé</strong> pour préserver vos quotas de stockage (les fichiers restent disponibles sur vos postes de travail locaux).
                                    Vous pouvez également lancer un nettoyage immédiat de tous les cas déjà facturés.
                                </p>
                                <button
                                    className="btn btn-outline-warning d-flex align-items-center gap-2 px-4 shadow-sm fw-bold"
                                    onClick={handlePurgeInvoicedSTL}
                                    disabled={purgingSTL}
                                    style={{ borderRadius: '10px' }}
                                >
                                    {purgingSTL ? <FaSpinner className="fa-spin" /> : <FaTrashAlt />}
                                    Nettoyer les fichiers 3D des cas facturés
                                </button>
                                {purgeResult && (
                                    <div className="alert border-0 small mt-3 mb-0 d-flex align-items-center" style={{ background: '#ecfdf5', color: '#065f46', borderRadius: '8px' }}>
                                        <FaCheck className="me-2 flex-shrink-0" />
                                        <span>{purgeResult}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* User Management Section */}
                <div className="settings-card shadow-sm border-0" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-white border-bottom-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold d-flex align-items-center">
                            <FaUsers className="text-primary me-2" /> Utilisateurs & Rôles
                        </h5>
                        <button
                            className="btn btn-sm btn-primary d-flex align-items-center gap-1 shadow-sm fw-bold"
                            onClick={() => {
                                setShowAddForm(!showAddForm);
                                setErrorMsg('');
                                setSuccessMsg('');
                            }}
                            style={{ borderRadius: '8px', padding: '6px 12px' }}
                        >
                            <FaPlus size={10} /> Ajouter un utilisateur
                        </button>
                    </div>
                    <div className="card-body p-4">
                        {showAddForm && (
                            <form onSubmit={handleAddUser} className="mb-4 p-4 bg-light rounded animate__animated animate__fadeIn" style={{ border: '1px solid #e5e7eb', borderRadius: '12px' }}>
                                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                    <FaPlus size={12} className="text-primary" /> Créer un nouvel utilisateur
                                </h6>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Prénom"
                                            value={newUser.firstName}
                                            onChange={e => setNewUser({ ...newUser, firstName: e.target.value })}
                                            required
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Nom"
                                            value={newUser.lastName}
                                            onChange={e => setNewUser({ ...newUser, lastName: e.target.value })}
                                            required
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <input
                                            type="email"
                                            className="form-control form-control-sm"
                                            placeholder="Adresse Email"
                                            value={newUser.email}
                                            onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                                            required
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <input
                                            type="password"
                                            className="form-control form-control-sm"
                                            placeholder="Mot de passe (min 6 caractères)"
                                            value={newUser.password}
                                            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                            required
                                            minLength={6}
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="text-muted small fw-bold mb-1">Rôle initial</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={newUser.role}
                                            onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            <option value="producteur">Producteur (Sous-traitant)</option>
                                            <option value="client">Client (Cabinet dentaire)</option>
                                            <option value="admin">Administrateur</option>
                                        </select>
                                    </div>
                                </div>
                                {errorMsg && <div className="alert alert-danger py-2 px-3 small mb-3" style={{ borderRadius: '8px' }}>{errorMsg}</div>}
                                <div className="d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-sm btn-outline-secondary px-3" onClick={() => setShowAddForm(false)} style={{ borderRadius: '8px' }}>
                                        Annuler
                                    </button>
                                    <button type="submit" className="btn btn-sm btn-primary px-3 fw-bold" disabled={creatingUser} style={{ borderRadius: '8px' }}>
                                        {creatingUser ? <FaSpinner className="fa-spin me-1" style={{ animation: 'spin 1s linear infinite' }} /> : null}
                                        Créer
                                    </button>
                                </div>
                            </form>
                        )}

                        {successMsg && <div className="alert alert-success py-2 px-3 small mb-3 animate__animated animate__fadeIn" style={{ borderRadius: '8px' }}>{successMsg}</div>}

                        {loadingProfiles ? (
                            <div className="text-center py-5">
                                <FaSpinner className="fa-spin text-primary mb-3" size={32} style={{ animation: 'spin 1s linear infinite' }} />
                                <p className="text-muted mb-0">Chargement des utilisateurs...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive" style={{ borderRadius: '12px', overflowX: 'auto', border: '1px solid #e5e7eb' }}>
                                    <table className="table table-hover mb-0" style={{ fontSize: '0.9rem', minWidth: '650px' }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb' }}>
                                                <th className="fw-bold text-muted small text-uppercase py-3 px-3" style={{ letterSpacing: '0.05em' }}>Email</th>
                                                <th className="fw-bold text-muted small text-uppercase py-3 px-3" style={{ letterSpacing: '0.05em' }}>Nom</th>
                                                <th className="fw-bold text-muted small text-uppercase py-3 px-3" style={{ letterSpacing: '0.05em' }}>Rôle</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profiles.map(profile => {
                                                const badgeStyle = roleBadgeStyles[profile.role] || roleBadgeStyles.client;
                                                const icon = roleIcons[profile.role] || roleIcons.client;
                                                return (
                                                    <tr key={profile.id}>
                                                        <td className="py-3 px-3 align-middle">
                                                            <span className="fw-semibold">{profile.email || '—'}</span>
                                                        </td>
                                                        <td className="py-3 px-3 align-middle text-muted">
                                                            {profile.full_name || '—'}
                                                        </td>
                                                        <td className="py-3 px-3 align-middle">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span
                                                                    className="d-inline-flex align-items-center gap-1 px-2 py-1 fw-bold small"
                                                                    style={{
                                                                        ...badgeStyle,
                                                                        borderRadius: '8px',
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {icon} {profile.role}
                                                                </span>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={profile.role}
                                                                    onChange={(e) => handleRoleChange(profile.id, e.target.value)}
                                                                    disabled={savingRole === profile.id}
                                                                    style={{
                                                                        borderRadius: '8px',
                                                                        width: 'auto',
                                                                        minWidth: '130px',
                                                                        fontSize: '0.85rem',
                                                                        border: '1px solid #d1d5db',
                                                                    }}
                                                                >
                                                                    <option value="admin">Admin</option>
                                                                    <option value="client">Client</option>
                                                                    <option value="producteur">Producteur</option>
                                                                </select>
                                                                {savingRole === profile.id && (
                                                                    <FaSpinner className="text-primary" size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-3 d-flex align-items-center gap-2">
                                    <FaCheck className="text-success" size={14} />
                                    <span className="text-muted small">
                                        <strong>{profiles.length}</strong> utilisateur{profiles.length > 1 ? 's' : ''} au total
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;

